import { analyzeAnswers } from './analytics';
import {
  buildAdditionPool,
  buildAdditionPoolFromSpec,
  buildMultiplicationPool,
  buildMultiplicationPoolFromSpec,
  questionKey,
  shuffle,
  toQuestion
} from './question';
import type { CompletedAnswer, Operator, Question, WeakCategory } from './types';
import type { YearConfig } from './year-config';

export interface GenerateFollowUpOptions {
  answers: CompletedAnswer[];
  weakCategories: WeakCategory[];
  questionCount?: number;
  random?: () => number;
  yearConfig?: YearConfig;
}

export function generateFollowUpQuestions(options: GenerateFollowUpOptions): Question[] {
  const questionCount = options.questionCount ?? 15;
  const random = options.random ?? Math.random;
  const additionPool = additionPoolFor(options.yearConfig);
  const multiplicationPool = multiplicationPoolFor(options.yearConfig);
  const selected: Question[] = [];
  const selectedKeys = new Set<string>();
  const topCategories = options.weakCategories.slice(0, 3);
  const weakTarget = Math.round(questionCount * 0.7);
  const slowTarget = questionCount - weakTarget;

  addUnique(
    selected,
    selectedKeys,
    shuffle(
      topCategories.flatMap((category) => poolForCategory(category.label, additionPool, multiplicationPool)),
      random
    ),
    weakTarget
  );

  const allowedKeys = new Set([...additionPool, ...multiplicationPool].map(questionKey));
  const analysedAnswers = analyzeAnswers(options.answers).answers;
  const slowQuestions = analysedAnswers
    .filter((answer) => answer.isCorrect && answer.isSlow)
    .map((answer) => relatedQuestion(answer))
    .filter((question) => allowedKeys.has(questionKey(question)));
  addUnique(selected, selectedKeys, shuffle(slowQuestions, random), slowTarget);

  const weakestOperand = parseCategory(topCategories[0]?.label)?.operand;
  if (weakestOperand) {
    addUnique(
      selected,
      selectedKeys,
      shuffle(
        multiplicationPool.filter(
          (question) => question.operand1 === weakestOperand || question.operand2 === weakestOperand
        ),
        random
      ),
      questionCount
    );
  }

  addUnique(selected, selectedKeys, shuffle(multiplicationPool, random), questionCount);
  addUnique(selected, selectedKeys, shuffle(additionPool, random), questionCount);

  // A small year may offer fewer unique facts than requested; repeat facts so the practice
  // session still has the requested length (repetition is acceptable for practice).
  if (selected.length < questionCount && additionPool.length + multiplicationPool.length > 0) {
    const padding = shuffle([...additionPool, ...multiplicationPool], random);
    for (let index = 0; selected.length < questionCount; index += 1) {
      selected.push(padding[index % padding.length]);
    }
  }

  return selected.slice(0, questionCount).map((question, index) => ({ ...question, position: index + 1 }));
}

function additionPoolFor(yearConfig?: YearConfig): Question[] {
  if (!yearConfig) {
    return buildAdditionPool();
  }
  return yearConfig.addition ? buildAdditionPoolFromSpec(yearConfig.addition) : [];
}

function multiplicationPoolFor(yearConfig?: YearConfig): Question[] {
  if (!yearConfig) {
    return buildMultiplicationPool();
  }
  return yearConfig.multiplication ? buildMultiplicationPoolFromSpec(yearConfig.multiplication) : [];
}

function addUnique(
  selected: Question[],
  selectedKeys: Set<string>,
  candidates: Question[],
  targetLength: number
): void {
  for (const candidate of candidates) {
    if (selected.length >= targetLength) {
      return;
    }
    const key = questionKey(candidate);
    if (!selectedKeys.has(key)) {
      selected.push(candidate);
      selectedKeys.add(key);
    }
  }
}

function poolForCategory(label: string, additionPool: Question[], multiplicationPool: Question[]): Question[] {
  const parsed = parseCategory(label);
  if (!parsed) {
    return [];
  }
  const pool = parsed.operator === 'x' ? multiplicationPool : additionPool;
  return pool.filter((question) => question.operand1 === parsed.operand || question.operand2 === parsed.operand);
}

function parseCategory(label: string | undefined): { operator: Operator; operand: number } | null {
  const match = /^([x+])(\d+) facts$/.exec(label ?? '');
  if (!match) {
    return null;
  }
  return { operator: match[1] as Operator, operand: Number(match[2]) };
}

function relatedQuestion(answer: CompletedAnswer): Question {
  const operand = answer.operator === 'x' ? Math.max(answer.operand1, answer.operand2) : answer.operand2;
  const partner = Math.max(1, Math.min(answer.operator === 'x' ? 12 : 9, answer.operand1));
  return toQuestion(0, operand, answer.operator, partner);
}
